import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import cn from 'classnames';

// Redux
import { useGetFoodCategoriesQuery } from '../../../redux/slices/foodCategorySlice';

// Components
import DateFilter from '../../form/DateFilter';
import SearchFilterModal from './SearchFilterModal';

// Styles
import dateFilterSearchTheme from '../../form/DateFilter-search.module.postcss';
import styles from './SearchFilters.module.postcss';

function SearchFilters({ filters, setFilters, mapOpen, setMapOpen }) {
  const [tomorrow, setTomorrow] = useState(false);
  const [openToday, setOpenToday] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);
  const [selectedCuisine, setSelectedCuisine] = useState(null);
  const [filterModalOpen, setFilterModalOpen] = useState(false);

  const { data = {} } = useGetFoodCategoriesQuery();
  const foodCategories = data.food_categories || [];

  const hasDateFilters = !!(filters.open_today || filters.tomorrow || filters.day);

  const onChangeCuisine = (newSelected) => {
    const ids = newSelected.map((c) => c.id);
    setFilters({ food_categories: ids.length ? ids.join(',') : null, page: null });
  };

  // Sync selectedCuisine from URL — URL stores comma-joined ids e.g. "mexican,bbq"
  useEffect(() => {
    const categories = data.food_categories || [];
    const raw = filters.food_categories;
    let newCuisine = [];
    if (raw) {
      const ids = String(raw).split(',').map((s) => s.trim()).filter(Boolean);
      newCuisine = ids.map((id) =>
        categories.find(
          (c) => c.id === id || (c.name && c.name.toLowerCase() === id.toLowerCase())
        )
      ).filter(Boolean);
    }
    if (JSON.stringify(newCuisine) !== JSON.stringify(selectedCuisine)) {
      setSelectedCuisine(newCuisine);
    }
  }, [filters.food_categories, data.food_categories, selectedCuisine]);

  // Update selectedDay
  useEffect(() => {
    const newSelectedDay = filters.day ? dayjs(filters.day).toDate() : null;
    if (newSelectedDay?.toDateString() !== selectedDay?.toDateString()) {
      setSelectedDay(newSelectedDay);
    }
  }, [filters.day, selectedDay]);

  const onChangeDate = (date) => {
    if (date) {
      setFilters({
        day: dayjs(date).format('YYYY-MM-DD'),
        open_today: null,
        open_now: null,
        tomorrow: null,
        page: null,
      });
    } else {
      setFilters({ day: null, page: null });
    }
  };

  const onOpenToday = () => {
    if (filters.open_today) {
      setFilters({ open_today: null, open_now: null, page: null });
    } else {
      setFilters({ open_today: true, open_now: null, tomorrow: null, day: null, page: null });
    }
  };

  const onOpenTomorrow = () => {
    if (filters.tomorrow) {
      setFilters({ tomorrow: null, page: null });
    } else {
      setFilters({ tomorrow: true, open_today: null, open_now: null, day: null, page: null });
    }
  };

  useEffect(() => { setOpenToday(filters.open_today); }, [filters.open_today]);
  useEffect(() => { setTomorrow(filters.tomorrow); }, [filters.tomorrow]);

  const activeFilterCount = (selectedCuisine || []).length;

  return (
    <>
      <div className={cn(styles.SearchFilters_Container, { [styles.SearchFilters_Container__mapOpen]: mapOpen }, 'Section')}>
        <div className={styles.SearchFilters}>
          <button
            type='button'
            className={cn('Button', { [styles.SearchFilters_Button__active]: openToday })}
            onClick={onOpenToday}
          >
            Open today
          </button>

          <button
            type='button'
            className={cn('Button', { [styles.SearchFilters_Button__active]: tomorrow })}
            onClick={onOpenTomorrow}
          >
            Tomorrow
          </button>

          <DateFilter
            theme={dateFilterSearchTheme}
            onChange={onChangeDate}
            selectedDay={selectedDay}
            placeholder='Choose a date'
            ariaLabel='Filter by date'
          />

          {/* Filter icon button — at end of pill row */}
          <button
            type='button'
            className={cn('Button', styles.SearchFilters_FilterBtn, {
              [styles.SearchFilters_FilterBtn__active]: activeFilterCount > 0,
            })}
            onClick={() => setFilterModalOpen(true)}
            aria-label='Open filters'
          >
            <svg width='14' height='14' viewBox='0 0 14 14' fill='none' xmlns='http://www.w3.org/2000/svg' aria-hidden='true'>
              <line x1='1' y1='3' x2='13' y2='3' stroke='currentColor' strokeWidth='1.5' strokeLinecap='round'/>
              <line x1='1' y1='7' x2='13' y2='7' stroke='currentColor' strokeWidth='1.5' strokeLinecap='round'/>
              <line x1='1' y1='11' x2='13' y2='11' stroke='currentColor' strokeWidth='1.5' strokeLinecap='round'/>
              <circle cx='4' cy='3' r='1.75' fill='white' stroke='currentColor' strokeWidth='1.25'/>
              <circle cx='9' cy='7' r='1.75' fill='white' stroke='currentColor' strokeWidth='1.25'/>
              <circle cx='5' cy='11' r='1.75' fill='white' stroke='currentColor' strokeWidth='1.25'/>
            </svg>
            Filters
            {activeFilterCount > 0 && (
              <span className={styles.SearchFilters_FilterBadge}>{activeFilterCount}</span>
            )}
          </button>
        </div>

        {/* Desktop-only map toggle — single pill button */}
        { hasDateFilters &&
          <button
            type='button'
            className={cn(styles.ViewToggle_Btn, { [styles.ViewToggle_Btn__active]: mapOpen })}
            onClick={() => setMapOpen(!mapOpen)}
            aria-pressed={mapOpen}
          >
            <svg width='14' height='14' viewBox='0 0 14 14' fill='none' xmlns='http://www.w3.org/2000/svg' aria-hidden='true'>
              <path d='M7 1a4 4 0 0 0-4 4c0 3 4 8 4 8s4-5 4-8a4 4 0 0 0-4-4zm0 5.5A1.5 1.5 0 1 1 7 3a1.5 1.5 0 0 1 0 3.5z' fill='currentColor'/>
            </svg>
            {mapOpen ? 'Hide map' : 'Show map'}
          </button>
        }
      </div>

      <SearchFilterModal
        isOpen={filterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        foodCategories={foodCategories}
        selected={selectedCuisine || []}
        onChange={onChangeCuisine}
      />
    </>
  );
}

SearchFilters.propTypes = {
  filters: PropTypes.object.isRequired,
  setFilters: PropTypes.func.isRequired,
};

export default SearchFilters;
